import './styles/_quick-view.scss';

import { getPDPContent, loadJavaScript } from '../utils/product';

const state = {
  variables: {
    containerName: 'quick-view-container',
    handle: null,
  },
  elements: {
    closeButtonSvg: null,
  },
};

/**
 * Dispatches the 'quick-view::close' event
 */
export const closeQuickView = () => {
  document.dispatchEvent(new CustomEvent('quick-view::close'));
};

/**
 * Dispatches the 'product::quick-view::open' event
 */
export const openQuickView = (data) => {
  const parsedData = JSON.parse(data);
  const { product } = parsedData;
  const { handle } = product;

  window.product = product;
  document.dispatchEvent(
    new CustomEvent('quick-view::open', {
      detail: {
        handle,
      },
    })
  );
};

/**
 * Caches DOM elements to avoid multiple lookups
 */
const cacheDOMElements = () => {
  state.elements.closeButtonSvg = document
    .querySelector('.search_btn--close svg')
    ?.cloneNode(true);
};

/**
 * Creates the close button for the quick view modal
 * @returns {HTMLElement} The close button element
 */
const createCloseButton = () => {
  const closeButton = document.createElement('button');
  closeButton.classList.add('quick-view-close');
  closeButton.setAttribute('data-close-quick-view', '');

  if (state.elements.closeButtonSvg) {
    closeButton.appendChild(state.elements.closeButtonSvg);
  }

  return closeButton;
};

/**
 * Pushes related products to the global window object
 * @param relatedProducts
 */
const pushRelatedProducts = (relatedProducts) => {
  window.relatedProducts = [];

  relatedProducts.forEach((relatedProduct) => {
    const obj = {
      ...relatedProduct.product,
      video: relatedProduct.video,
    };
    window.relatedProducts.push(obj);
  });
};

/**
 * Handles the opening of the quick view modal
 * @param {Event} e - The event object containing details for the quick view
 */
const handleQuickViewOpen = async (e) => {
  const { handle } = e.detail;
  let quickViewContainer = document.querySelector(
    `.${state.variables.containerName}`
  );

  if (state.variables.handle !== handle) {
    const content = await getPDPContent(handle);
    const relatedProducts = JSON.parse(
      content.querySelector('#related-products-json').textContent
    );
    pushRelatedProducts(relatedProducts);

    const jsLinks = content.querySelectorAll('[data-js-quick-view]');

    if (!quickViewContainer) {
      quickViewContainer = createQuickViewContainer(content.body.innerHTML);
      document.body.appendChild(quickViewContainer);
    } else {
      quickViewContainer.querySelector('.quick-view-content').innerHTML =
        content.body.innerHTML;
    }
    jsLinks.forEach((link) => {
      loadJavaScript(link.src);
    });
  }
  showQuickViewContainer(quickViewContainer);
};

/**
 * Creates the quick view container and inserts content
 * @param {string} contentHTML - The HTML content to be displayed in the quick view
 * @returns {HTMLElement} The quick view container element
 */
const createQuickViewContainer = (contentHTML) => {
  const quickViewContainer = document.createElement('div');
  quickViewContainer.classList.add(state.variables.containerName);
  quickViewContainer.innerHTML = `
    ${createCloseButton().outerHTML}
    <div class="quick-view-content">${contentHTML}</div>
  `;
  return quickViewContainer;
};

/**
 * Shows the quick view container and locks body scroll
 * @param {HTMLElement} container - The quick view container element
 */
const showQuickViewContainer = (container) => {
  container.classList.add('show');
  document.body.classList.add('overflow-hidden--quick-view');
};

/**
 * Hides and optionally clears the quick view container
 * @param {boolean} clearContent - If true, the content will be removed from the DOM
 */
const handleQuickViewClose = (clearContent = false) => {
  const quickViewContainer = document.querySelector(
    `.${state.variables.containerName}`
  );
  if (!quickViewContainer) return;

  quickViewContainer.classList.add('closing');
  setTimeout(() => {
    quickViewContainer.classList.remove('closing');
    document.body.classList.remove('overflow-hidden--quick-view');
    quickViewContainer.classList.remove('show');
    if (clearContent) {
      quickViewContainer.remove();
    }
  }, 300);
};

/**
 * Handles click events on the quick view container backdrop
 * @param {Event} e - The click event
 */
const handleBackdropClick = (e) => {
  const { target } = e;
  const isArrow = target?.closest('.swiper-control');
  if (isArrow) return;

  if (
    target.classList.contains(state.variables.containerName) ||
    target.closest('[data-close-quick-view]')
  ) {
    closeQuickView();
    return;
  }
  if (target.closest('[data-open-quick-view]')) {
    const handle = target.closest('[data-open-quick-view]').dataset
      .openQuickView;
    const data = document.querySelector(`[data-product-obj="${handle}"]`);
    if (!data) return;

    openQuickView(data.textContent);
  }
};

/**
 * Adds event listeners for the quick view functionality
 */
const addEventListeners = () => {
  document.addEventListener('quick-view::open', handleQuickViewOpen);
  document.addEventListener('quick-view::close', () => handleQuickViewClose());
  document.addEventListener('click', handleBackdropClick);
};

/**
 * Initializes the product quick view module
 */
const initProductQuickView = () => {
  cacheDOMElements();
  addEventListeners();
};

export default initProductQuickView;
